/// <reference types="codemirror" />
/// <reference types="codemirror/addon/mode/simple.d.ts" />

import {
  atKeys,
  isNullish,
  notEmpty,
  parseArray,
  parseNumber,
  parseRecord,
  parseString,
} from "@de-xima/xima-common-js-lang";

import { getWidgetByVar, runRemoteCommand } from "./Util.ts";

/**
 * Adds a new mode to CodeMirror for highlighting boolean conditional infix expressions.
 * Used e.g. by the multiple condition node of the workflow.
 */
export function registerConditionalExpressionMode(): void {
  if (typeof window.CodeMirror === "function") {
    CodeMirror.defineSimpleMode("fc-conditional-expression", {
      start: [
        {
          regex:
            /(and|or|xor|nor|nand|not|implies|impliedby|equiv|unequiv|<=>|<->|=|==|===|!=|!==|=>|->|<=|<-|&|&&|\|\||\^|!)/i,
          token: "operator",
        },
        { regex: /(true|false|0|1)(?:[\s()]|$)/i, token: "atom" },
        { regex: /[^()\s<=>!-&|^]+/, token: "variable" },
      ],
    });
  }
}

/**
 * Adds code syntax highlighting to code mirror for
 * [RFC 2254](https://tools.ietf.org/html/rfc2254) (LDAP search filter syntax).
 */
export function registerRfc2254Mode(): void {
  if (typeof window.CodeMirror === "function") {
    const value = /([^&!|*><~:=*()\\/]|\\[0-9a-f]{2})+/;
    CodeMirror.defineSimpleMode("rfc2254", {
      meta: {
        electricChars: "()",
      },
      prop: [
        { regex: /\s+/, token: "space" },
        { regex: /\*/, token: "qualifier" },
        { regex: /:/, token: "qualifier" },
        { regex: /[&!|]/, token: "operator" },
        { regex: /=/, token: "operator" },
        { regex: /~=/, token: "operator" },
        { regex: />=/, token: "operator" },
        { regex: /<=/, token: "operator" },
        { indent: true, regex: /\(/, token: "bracket" },
        { dedent: true, next: "start", regex: /\)/, token: "bracket" },
        { regex: value, token: "string" },
      ],
      start: [
        {
          regex: /\s+/,
          token: "space",
        },
        {
          regex: /\*/,
          token: "qualifier",
        },
        {
          regex: /:/,
          token: "qualifier",
        },
        {
          regex: /[&!|]/,
          token: "operator",
        },
        {
          next: "prop",
          regex: /=/,
          token: "operator",
        },
        {
          next: "prop",
          regex: /~=/,
          token: "operator",
        },
        {
          next: "prop",
          regex: />=/,
          token: "operator",
        },
        {
          next: "prop",
          regex: /<=/,
          token: "operator",
        },
        {
          indent: true,
          regex: /\(/,
          token: "bracket",
        },
        {
          dedent: true,
          regex: /\)/,
          token: "bracket",
        },
        {
          regex: value,
          token: "attribute",
        },
      ],
    });
  } else {
    console.warn("No code mirror lib found");
  }
}

/**
 * Runs a linting process on the given code mirror instance.
 * @param widgetVar Widget variable of the code mirror widget.
 */
export function performLint(widgetVar: string): void {
  if (!("ExtCodeMirror" in PrimeFaces.widget)) {
    return;
  }
  const widget = getWidgetByVar(widgetVar, PrimeFaces.widget.ExtCodeMirror);
  if (widget === undefined) {
    console.warn("No code mirror widget found, cannot setup linting.");
    return;
  }
  const cm = widget.getCodeMirrorInstance();
  if (isNullish(cm)) {
    console.warn("No code mirror instance found, cannot setup linting.");
    return;
  }
  cm.performLint();
}

/**
 * Enables linting for a code mirror instance via a JSF bean callback. The parameters passed to the
 * remote command can be accessed on the server via
 * ```java
 * FacesContext.getCurrentInstance().getExternalContext().getRequestParameterMap().get("paramName");
 * ```
 * Currently, the following parameters are passed to the remote command:
 * - `lintContent` - The entire code entered in the editor that is to be linted.
 * The remote command must return a list of objects representing problems to be shown in the editor. The
 * server can return values via
 * ```java
 * PrimeFaces.current().ajax().addCallbackParam("paramName", paramValue);
 * ```
 *
 * @param widgetVar Widget variable of the code mirror instance to for which to enable linting.
 * @param remoteCommand Remote command that performs the linting.
 */
export function setupLinting(
  widgetVar: string,
  remoteCommand: PrimeFaces.ajax.RemoteCommand<{ lintCode: string }, { lintResult: string }>,
): void {
  if (!("ExtCodeMirror" in PrimeFaces.widget)) {
    return;
  }
  const widget = getWidgetByVar(widgetVar, PrimeFaces.widget.ExtCodeMirror);
  if (widget === undefined) {
    console.warn("No code mirror widget found, cannot setup linting.");
    return;
  }
  const cm = widget.getCodeMirrorInstance();
  if (isNullish(cm)) {
    console.warn("No code mirror instance found, cannot setup linting.");
    return;
  }
  // Linting was already set up
  if (isNullish(cm.getOption("lint"))) {
    return;
  }
  const lintOptions: CodeMirror.LintStateOptions = {
    async: true,
    getAnnotations: async (content, callback, _opts, instance) => {
      const response = await runRemoteCommand(remoteCommand, [{ name: "lintCode", value: content }]);
      const annotations = [] as CodeMirror.Annotation[];

      if (notEmpty(response.lintResult)) {
        const lintResult = JSON.parse(response.lintResult) as unknown;
        for (const item of parseArray(atKeys(lintResult, ["annotations"]))) {
          const annotation = parseRecord(item);
          annotations.push({
            from: new window.CodeMirror.Pos(parseNumber(annotation.fromLine), parseNumber(annotation.fromColumn)),
            message: parseString(annotation.message),
            severity: parseString(annotation.severity),
            to: new window.CodeMirror.Pos(parseNumber(annotation.toLine), parseNumber(annotation.toColumn)),
          });
        }
      }
      callback(instance, annotations);
    },
    hasGutters: true,
  };
  cm.setOption("lint", lintOptions);
}
